/**
 * Staff Service - Handles staff information loading with retry logic
 */

import type { ICurrentStaffInfo } from "@/pages/AdditionalInfo/types";
import staffService, { getCurrentStaff } from "@/services/staff";
import type { UserProfile } from "@/stores/types";
import { Logger } from "@/utils";

// Constants
const TIMEOUT_SYMBOL = Symbol("timeout");
const STAFF_LOAD_TIMEOUT_MS = 10000;
const STAFF_RETRY_MAX_ATTEMPTS = 3;

/**
 * Map ICurrentStaffInfo to UserProfile
 * Converts staff API response to user profile format
 */
export const mapStaffInfoToUserProfile = (
  staffInfo: ICurrentStaffInfo,
  permissions?: string[],
): Partial<UserProfile> => {
  return {
    // Basic identifiers
    id: staffInfo.userId, // Use userId as the primary ID
    userId: staffInfo.userId,
    staffId: staffInfo.id, // Store the numeric staff ID
    username: staffInfo.username,
    name: staffInfo.fullName || staffInfo.username,
    email: staffInfo.email,

    // Staff details
    fullName: staffInfo.fullName,
    idNumber: staffInfo.idNumber || undefined,
    employeeId: staffInfo.employeeId || undefined,
    phoneNumber: staffInfo.phoneNumber || undefined,
    phoneNumberCountryCode: staffInfo.phoneNumberCountryCode || undefined,
    specialtyId: staffInfo.specialtyId || undefined,
    specialty: staffInfo.specialty, // Object with {id, code, description}
    locations: staffInfo.locations,
    startDate: staffInfo.startDate,
    note: staffInfo.note || undefined,

    // Audit fields
    createdAt: staffInfo.createdAt,
    createdBy: staffInfo.createdBy,
    lastLogin: staffInfo.lastLogin || undefined,
    creatorName: staffInfo.creatorName || undefined,
    status: staffInfo.status,

    // Permissions
    permissions: permissions || [],
  };
};

/**
 * Load user permissions with retry logic
 * Returns empty array on failure
 */
export const loadUserPermissions = async (): Promise<string[]> => {
  try {
    const permissions = await staffService.checkPermission();
    return permissions.map((p) => p.name);
  } catch (error) {
    Logger.error("Failed to load user permissions:", error);
    return [];
  }
};

/**
 * Load staff information with retry logic and timeout
 * Returns null on failure to not block the login process
 */
export const loadStaffWithRetry = async (): Promise<ICurrentStaffInfo | null> => {
  const retryGetCurrentStaff = async () => {
    for (let attempt = 1; attempt <= STAFF_RETRY_MAX_ATTEMPTS; attempt++) {
      try {
        const response = await getCurrentStaff({
          IncludeSpecialty: true,
          IncludeLastLogin: true,
          IncludeCreatorName: true,
          IncludeLocations: true,
        });
        return response.data;
      } catch (error) {
        if (attempt === STAFF_RETRY_MAX_ATTEMPTS) {
          throw error;
        }
        // Exponential backoff: 1s, 2s, 3s
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      }
    }
    return null;
  };

  try {
    const result = await Promise.race([
      retryGetCurrentStaff(),
      new Promise<null>((_, reject) =>
        setTimeout(() => reject(TIMEOUT_SYMBOL), STAFF_LOAD_TIMEOUT_MS),
      ),
    ]);
    return result;
  } catch (error) {
    if (error === TIMEOUT_SYMBOL) {
      Logger.warn(
        `Staff API timed out after ${STAFF_LOAD_TIMEOUT_MS}ms, continuing without staff info`,
      );
      return null;
    }
    Logger.error("Failed to load staff info:", error);
    return null;
  }
};

/**
 * Refresh user profile with complete staff information
 * Loads staff info and permissions, then returns mapped UserProfile
 */
export const refreshUserProfile = async (): Promise<Partial<UserProfile> | null> => {
  try {
    // Load staff info and permissions in parallel
    const [staffInfo, permissions] = await Promise.all([
      loadStaffWithRetry(),
      loadUserPermissions(),
    ]);

    if (!staffInfo) {
      Logger.warn("Failed to refresh user profile: staff info unavailable");
      return null;
    }

    // Map to UserProfile format
    const userProfile = mapStaffInfoToUserProfile(staffInfo, permissions);
    Logger.info("User profile refreshed successfully");
    return userProfile;
  } catch (error) {
    Logger.error("Failed to refresh user profile:", error);
    return null;
  }
};
